import {
  computed,
  effect,
  Signal,
  signal,
  type ReadonlySignal,
} from "@preact/signals";
import { z } from "zod";
import type { LoginManager, UserProfile } from "./LoginManager";
import {
  getUserAnimalVisual,
  type BibleReadingSession,
  type ConnectedSessionUser,
  type ConnectionSessionUserVisual,
} from "./SessionsManager";
import type { TranslatableTitle } from "./BibleToolsManager";
import { translateTitle } from "../app/utils";
import type { VerseRef } from "./BibleDataManager";
import { scanVerseReferencesInText } from "./BibleDataManager";
import type { TranslationBook } from "./FreeUseBibleAPI";
import { getConnectedUserVisualKey } from "./SessionsManager";
import { v4 as uuid } from "uuid";
import type { I18nManager } from "../i18n/I18nManager";
import { type i18n } from "i18next";
import type { AIProviderFunctionTool } from "./AIManager";

export const chatMessageBaseSchema = z.object({
  /**
   * The ID of the message, which should be unique within the chat session.
   */
  id: z.string(),

  /**
   * The IDs of the participants who sent the message.
   * If empty, the message is considered anonymous and has no author.
   */
  authors: z.array(z.string()),

  /**
   * The unix time in milliseconds when the message was sent.
   */
  timeMs: z.number().int().nonnegative(),

  /**
   * The IDs of the participants targeted by the message, or true if the message targets everyone.
   */
  targets: z.union([z.array(z.string()), z.literal(true)]),
});

export const textChatMessageSchema = chatMessageBaseSchema.extend({
  type: z.literal("text"),
  text: z.string(),
});

export const toolCallChatMessageSchema = chatMessageBaseSchema.extend({
  type: z.literal("tool_call"),
  name: z.string(),
});

export const chatMessageSchema = z.discriminatedUnion("type", [
  textChatMessageSchema,
  toolCallChatMessageSchema,
]);

export type ChatMessageBase = z.infer<typeof chatMessageBaseSchema>;
export type TextChatMessage = z.infer<typeof textChatMessageSchema>;
export type ChatMessage = z.infer<typeof chatMessageSchema>;

export const chatMessageOptionsSchema = z.discriminatedUnion("type", [
  textChatMessageSchema.omit({
    timeMs: true,
    id: true,
    authors: true,
    targets: true,
  }),
  toolCallChatMessageSchema.omit({
    timeMs: true,
    id: true,
    authors: true,
    targets: true,
  }),
]);

export type ChatMessageOptions = z.infer<typeof chatMessageOptionsSchema>;

export type ChatProviderTextStream =
  | Iterable<string>
  | AsyncIterable<string>
  | Iterator<string>
  | AsyncIterator<string>;

export interface StreamingTextChatMessageOptions {
  type: "text";
  text: ChatProviderTextStream;
}

export type ChatProviderMessageOptions =
  | ChatMessageOptions
  | StreamingTextChatMessageOptions;

/**
 * A stream of messages that a {@link ChatProvider} can return from
 * `generateResponse` instead of a single message, e.g. to narrate multiple
 * turns of a tool-calling loop. Each yielded message may itself be a
 * {@link StreamingTextChatMessageOptions} with progressively-streamed text.
 */
export type ChatProviderMessageStream =
  | Iterable<ChatProviderMessageOptions>
  | AsyncIterable<ChatProviderMessageOptions>
  | Iterator<ChatProviderMessageOptions>
  | AsyncIterator<ChatProviderMessageOptions>;

export type ChatProviderResponse =
  | ChatProviderMessageOptions
  | ChatProviderMessageStream;

export interface ParsedChatTextMessage extends ChatMessageBase {
  type: "text";
  /** The original text of the message. */
  text: string;

  /**
   * The parts of the text.
   */
  parts: ParsedTextPart[];
}

export type ParsedTextPart =
  | string
  | ParsedTextMentionPart
  | ParsedVerseReferencePart;
export interface ParsedTextMentionPart {
  type: "mention";
  text: string;
  participant: ChatParticipant | null;
}

export interface ParsedVerseReferencePart {
  type: "verse_reference";
  /**
   * The original text of the verse reference, e.g. "John 3:16-17".
   */
  text: string;

  /** The verse reference. */
  ref: VerseRef;
}

/**
 * Custom context that a local chat session can provide to its AI chat providers.
 */
export interface LocalChatContext {
  /**
   * The instructions (system prompt) that should be passed to AI models.
   * If omitted, the chat provider can choose its own instructions.
   */
  instructions?: string;

  /**
   * The tools that should be provided to the AI model for this chat.
   */
  tools?: AIProviderFunctionTool[];
}

/**
 * A {@link LocalChatContext} that carries an `id` so it can be added to and
 * later removed from a chat session via `addContext`/`removeContext`.
 */
export interface IdentifiedLocalChatContext extends LocalChatContext {
  /**
   * A unique identifier used to add or remove this context. Adding a context
   * with an `id` that is already present replaces the existing one.
   */
  id: string;

  /**
   * The label for the context, which can be used to display a description of the context to the user.
   */
  label: TranslatableTitle;

  /**
   * An optional action shown alongside this context's row in the "Active AI
   * context" menu (e.g. a settings gear) — lets a context-contributing
   * extension also offer a way to configure itself, with no core knowledge
   * of that extension.
   */
  settingsAction?: {
    label: TranslatableTitle;
    onClick: () => void;
  };
}

export interface ChatContext {
  chatId: string;
  messages: ChatMessage[];
  participant: ChatParticipant;
  participants: ChatParticipant[];

  /**
   * The instructions (system prompt) that should be passed to AI models, if any.
   */
  instructions?: string;

  /**
   * The tools that should be provided to the AI model, if any.
   */
  tools?: AIProviderFunctionTool[];
}

export interface JoinLeaveChatContext {
  chatId: string;
  messages: ChatMessage[];
  participants: ChatParticipant[];

  /**
   * The instructions (system prompt) that should be passed to AI models, if any.
   */
  instructions?: string;

  /**
   * The tools that should be provided to the AI model, if any.
   */
  tools?: AIProviderFunctionTool[];
}

export interface ChatProvider {
  /** The name of the chat provider. */
  name: TranslatableTitle;
  /** The ID of the chat provider */
  id: string;

  /** An optional URL to an icon image for this provider. */
  iconUrl?: string;

  /** Whether this provider supports being added to shared chats. If false, then the provider can only be used in local (single user) chats. */
  supportsSharedChats: boolean;

  /** Whether this provider supports calling tools that are provided via {@link ChatContext.tools}. Defaults to false. */
  supportsToolCalling?: boolean;

  /**
   * Generates a response for the given chat context. May return a single
   * message, or an (async) iterable/iterator of messages to emit a sequence
   * of messages for one turn (e.g. across a multi-step tool-calling loop).
   * Each message, whether returned directly or yielded from a stream, may
   * itself stream its text progressively via {@link StreamingTextChatMessageOptions}.
   */
  generateResponse: (
    context: ChatContext
  ) => ChatProviderResponse | Promise<ChatProviderResponse | null> | null;
  /** Called when this provider is added as a participant to a chat. */
  onJoinChat?: (context: JoinLeaveChatContext) => void | Promise<void>;
  /** Called when this provider is removed as a participant from a chat. */
  onLeaveChat?: (context: JoinLeaveChatContext) => void | Promise<void>;
}

export interface BaseChatParticipant {
  /**
   * The ID of the participant.
   */
  id: string;

  /**
   * The display name of the participant. May be null if the participant is anonymous.
   */
  name: TranslatableTitle | null;

  /**
   * Whether this participant is the current user.
   */
  isSelf: boolean;

  /**
   * Whether this participant is an AI.
   */
  isAI: boolean;

  /**
   * Whether this participant is from a remote user.
   */
  isRemote: boolean;

  /**
   * Whether this participant is currently connected to the session.
   */
  isActive: boolean;

  /**
   * The unix time in milliseconds when this participant joined the chat.
   */
  joinTimeMs: number;
}

export interface UserChatParticipant extends BaseChatParticipant {
  /** The user ID for this participant, if known. */
  userId: string | null;
  /** The connection ID for this participant, if known. */
  connectionId: string | null;
  /** The user's profile information, if available. */
  profile?: UserProfile | null;

  /** The session user associated with this participant, if available. */
  sessionUser?: ConnectedSessionUser | null;

  /**
   * The visual information for this participant.
   */
  visual: ConnectionSessionUserVisual;

  isAI: false;
}

export interface AIChatParticipant extends BaseChatParticipant {
  /** The user ID that this AI participant is associated with, if any. */
  userId: string | null;
  /**
   * The connection ID that this AI participant is associated with, if any. This may be null even if userId is not null, in which case the participant is associated with the user but not with any specific connection of that user.
   */
  connectionId: string | null;

  /**
   * The ID of the participant that owns this AI participant.
   */
  ownerParticipantId: string;

  /** The ID of the AI provider. */
  providerId: string;

  /** An optional URL to an icon image for this AI participant's provider. */
  iconUrl?: string | null;

  isSelf: false;
  isAI: true;
}

export type ChatParticipant = UserChatParticipant | AIChatParticipant;

/**
 * True when this chat includes someone other than the local user and AI
 * providers — including people who are currently inactive or offline.
 * Surfaces use this to decide whether the local user's avatar needs the
 * animal+color combo so people can tell each other apart.
 *
 * Always reads `totalParticipants`, not the active-only `participants`
 * list, so a 1-on-1 chat still counts as "with other people" when the
 * other person is momentarily offline.
 */
export function chatHasOtherPeople(chat: {
  totalParticipants?: { readonly value: readonly ChatParticipant[] };
}): boolean {
  return (
    chat.totalParticipants?.value.some((p) => !p.isSelf && !p.isAI) ?? false
  );
}

const sharedAIChatParticipantSchema = z.object({
  id: z.string(),
  providerId: z.string(),
  name: z.string().nullable(),
  isAI: z.literal(true),
  iconUrl: z.string().optional(),
});

type SharedAIChatParticipant = z.infer<typeof sharedAIChatParticipantSchema>;

const sharedAIChatParticipantArraySchema = z.array(
  sharedAIChatParticipantSchema
);

/**
 * A translation the agent is asking this viewer to confirm. It lives on the
 * session in memory only, so a shared chat does not show the banner to anyone
 * else, and switching changes only this viewer's reader.
 */
export interface TranslationSuggestion {
  id: string;
  /** "Louis Segond (LSG)", or the short name when that is already the name. */
  label: string;
  shortName: string;
  /**
   * What the reader asked for when this suggestion is only the closest match
   * (for example "NIV"). Null when the suggestion is the translation they named.
   */
  unavailable: string | null;
}

export interface ChatSession {
  /**
   * The ID of the chat.
   */
  id: string;

  /** Chat messages ordered from oldest to most recent. */
  messages: ReadonlySignal<ChatMessage[]>;

  /** Parsed chat messages ordered from oldest to most recent. */
  parsedMessages: ReadonlySignal<ParsedChatTextMessage[]>;

  /**
   * Unread messages that have been sent since the last time the user marked messages as read.
   */
  unreadMessages: ReadonlySignal<ChatMessage[]>;
  /** The message ID of the latest message the user has read, if any. */
  lastMessageRead: ReadonlySignal<string | null>;

  /**
   * Whether any unread messages target the local participant.
   */
  wasMentioned: ReadonlySignal<boolean>;

  /**
   * Marks messages as read.
   * If `messageId` is provided, advances `lastMessageRead` to that ID only if it is more recent than the current value.
   * If omitted, advances `lastMessageRead` to the most recent message.
   */
  markAsRead: (messageId?: string) => void;
  /** Sends a message and notifies the other participants. */
  sendMessage: (message: ChatMessageOptions) => Promise<void>;

  /**
   * Inserts a message without asking an AI participant to reply.
   *
   * A confirmed or dismissed translation suggestion is recorded this way.
   * Going through {@link sendMessage} would start another turn.
   */
  appendMessage: (message: ChatMessageOptions, authors?: string[]) => void;

  /**
   * The translation banner for this chat, or null when there is nothing to
   * confirm. Not part of the message log.
   */
  translationSuggestion: Signal<TranslationSuggestion | null>;

  /** Updates whether the local participant is currently typing. */
  setTypingStatus: (isTyping: boolean) => void;
  /** Active participants only. */
  participants: ReadonlySignal<ChatParticipant[]>;
  /** All participants, including inactive ones. */
  totalParticipants: ReadonlySignal<ChatParticipant[]>;
  /**
   * Only inactive participants.
   */
  inactiveParticipants: ReadonlySignal<ChatParticipant[]>;
  /** Participants that can be added to this chat session. */
  availableParticipants: ReadonlySignal<ChatParticipant[]>;
  /** Participants currently typing. */
  typingParticipants: ReadonlySignal<ChatParticipant[]>;
  /** Adds a participant to this chat session. */
  addParticipant: (participantId: string) => void;
  /** Removes a participant from this chat session. */
  removeParticipant: (participantId: string) => void;

  /**
   * Gets the authors of a given message. Returns an empty array if the authors are anonymous or have left the session.
   * @param message The message to get the authors of.
   * @returns The authors of the message, or an empty array if the authors are anonymous or have left the session.
   */
  getMessageAuthors: (message: ChatMessage) => ChatParticipant[];

  /**
   * The final (merged) custom context provided to AI chat providers. This is
   * owned by the {@link ChatsManager} and shared across all chat sessions, so
   * a context added to the manager is automatically available here.
   */
  context: ReadonlySignal<LocalChatContext>;

  /**
   * Unsent text in this chat's compose field. Lives on the session so it
   * survives ChatView unmounting (clicking a verse closes the floating panel)
   * and a later Ask AI can append a quote instead of replacing what was typed.
   */
  unsentDraft: Signal<string>;
}

export interface SharedChatSession extends ChatSession {
  isShared: true;
  session: BibleReadingSession;
}

export interface ChatSessionHistory {
  messages: ChatMessage[];
  providerIds: string[];
}

export interface ChatsManager {
  isOpen: Signal<boolean>;
  chats: ReadonlySignal<ChatSession[]>;
  providers: ReadonlySignal<ChatProvider[]>;
  /** Total number of unread messages across all chat sessions. */
  numberOfUnreadMessages: ReadonlySignal<number>;
  /** Whether any unread message targets the local participant in any chat session. */
  wasMentioned: ReadonlySignal<boolean>;
  selectedChat: ReadonlySignal<ChatSession | null>;
  createSharedSession: (session: BibleReadingSession) => ChatSession;
  createLocalSession: (history?: ChatSessionHistory) => ChatSession;
  registerProvider: (provider: ChatProvider) => () => void;
  selectChat: (chatId: string | null) => void;

  /**
   * The final (merged) custom context provided to AI chat providers across all
   * chat sessions. Combines the default context (set via {@link setContext})
   * with any additional contexts (added via {@link addContext}).
   */
  context: ReadonlySignal<LocalChatContext>;

  /**
   * The identified contexts currently added via {@link addContext}, in the
   * order they were added. Unlike {@link context}, this preserves each
   * context's `id`/`label`/`tools` individually instead of merging them.
   */
  activeContexts: ReadonlySignal<IdentifiedLocalChatContext[]>;

  /**
   * Merges the given fields into the default custom context. Applies to every
   * chat session managed by this manager.
   * @param context The context fields to merge in.
   */
  setContext: (context: LocalChatContext) => void;

  /**
   * Adds an additional identified context that is merged into the default
   * context before being sent to AI chat providers. Adding a context whose
   * `id` is already present replaces the existing one.
   * @param context The identified context to add.
   */
  addContext: (context: IdentifiedLocalChatContext) => void;

  /**
   * Removes a previously added context by its `id`. Does nothing if no context
   * with that `id` is present.
   * @param id The id of the context to remove.
   */
  removeContext: (id: string) => void;

  /**
   * Text waiting to be inserted into the chat compose field. ChatView consumes
   * a non-empty value once (merges it into the draft if the field already has
   * content, then clears this signal) so callers like the verse toolbar's Ask
   * AI tool can prefill a question without owning the compose UI.
   */
  composerDraft: Signal<string>;
}

const DEFAULT_LOCAL_PARTICIPANT_ID = "local-user";

function getParticipantName(profile: UserProfile | null): string | null {
  const name = profile?.name;
  if (typeof name !== "string") {
    return null;
  }
  const trimmed = name.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function getConnectedUserName(user: {
  profile?: {
    name?: string | null;
  } | null;
}): string | null {
  const name = user.profile?.name;
  if (typeof name !== "string") {
    return null;
  }
  const trimmed = name.trim();
  return trimmed.length > 0 ? trimmed : null;
}

type GroupedConnectedUser = {
  id: string;
  userId: string | null;
  connectionId: string | null;
  profile: UserProfile | null;
  sessionUser: ConnectedSessionUser | null;
  name: string | null;
  isSelf: boolean;
  isActive: boolean;
  /**
   * The earliest time any of the user's connections joined the session, or
   * null if none of them broadcast a join time.
   */
  joinedAtMs: number | null;
};

function groupConnectedUsers(
  users: ConnectedSessionUser[]
): GroupedConnectedUser[] {
  const groups = new Map<string, ConnectedSessionUser[]>();

  for (const user of users) {
    const userId = user.userId ?? null;
    const connectionId = user.connectionId ?? null;
    const id = userId ?? connectionId;
    if (!id) {
      continue;
    }

    const group = groups.get(id);
    if (group) {
      group.push(user);
    } else {
      groups.set(id, [user]);
    }
  }

  return Array.from(groups.entries()).map(([id, group]) => {
    const representative = group.find((entry) => entry.isActive) ?? group[0]!;
    const joinTimes = group
      .map((entry) => entry.joinedAtMs)
      .filter((value): value is number => typeof value === "number");
    return {
      id,
      userId: representative.userId ?? null,
      connectionId: representative.connectionId ?? null,
      profile:
        group.find((entry) => entry.profile !== undefined)?.profile ?? null,
      sessionUser: representative,
      name:
        group
          .map((entry) => getConnectedUserName(entry))
          .find((name) => name) ?? null,
      isSelf: group.some((entry) => entry.isSelf),
      isActive: group.some((entry) => entry.isActive !== false),
      joinedAtMs: joinTimes.length > 0 ? Math.min(...joinTimes) : null,
    };
  });
}

function createChatMessage(
  options: ChatMessageOptions,
  authors: string[],
  targets: string[] | true,
  metadata?: {
    id?: string;
    timeMs?: number;
  }
): ChatMessage {
  const validMessage = chatMessageOptionsSchema.parse(options);
  return {
    id: metadata?.id ?? uuid(),
    timeMs: metadata?.timeMs ?? Date.now(),
    authors,
    targets,
    ...validMessage,
  };
}

function createProviderParticipantId(
  ownerConnectionId: string,
  providerId: string
): string {
  return `${ownerConnectionId}_${providerId}`;
}

/**
 * Combines the default chat context with any additional contexts into a single
 * {@link LocalChatContext} to hand to an AI chat provider. Instructions are
 * concatenated (non-empty, in order, separated by blank lines) and tool lists
 * are concatenated in order. Empty results collapse to `undefined` so a
 * provider can tell "no context" from "empty context".
 */
function mergeLocalChatContexts(
  contexts: LocalChatContext[]
): LocalChatContext {
  const instructions = contexts
    .map((context) => context.instructions)
    .filter(
      (instruction): instruction is string =>
        typeof instruction === "string" && instruction.trim().length > 0
    );
  const tools = contexts.flatMap((context) => context.tools ?? []);

  return {
    instructions:
      instructions.length > 0 ? instructions.join("\n\n") : undefined,
    tools: tools.length > 0 ? tools : undefined,
  };
}

function toAsyncIterator<T>(
  stream: Iterable<T> | AsyncIterable<T> | Iterator<T> | AsyncIterator<T>
): AsyncIterator<T> {
  if (
    typeof (stream as AsyncIterable<T>)[Symbol.asyncIterator] === "function"
  ) {
    return (stream as AsyncIterable<T>)[Symbol.asyncIterator]();
  }
  if (typeof (stream as Iterable<T>)[Symbol.iterator] === "function") {
    const iterator = (stream as Iterable<T>)[Symbol.iterator]();
    return {
      next: async () => iterator.next(),
    };
  }
  if (typeof (stream as AsyncIterator<T>).next === "function") {
    return stream as AsyncIterator<T>;
  }

  return {
    next: async () => ({
      done: true,
      value: undefined as unknown as T,
    }),
  };
}

/**
 * Distinguishes a single {@link ChatProviderMessageOptions} (always
 * discriminated by a `type` field) from a {@link ChatProviderMessageStream}
 * (an (async) iterable/iterator of messages, with no `type` field of its
 * own). Plain arrays of messages are supported since arrays are `Iterable`.
 */
function isProviderMessageStream(
  response: ChatProviderResponse
): response is ChatProviderMessageStream {
  if (typeof response === "object" && response !== null && "type" in response) {
    return false;
  }
  return (
    typeof (response as AsyncIterable<unknown>)[Symbol.asyncIterator] ===
      "function" ||
    typeof (response as Iterable<unknown>)[Symbol.iterator] === "function" ||
    typeof (response as Iterator<unknown>).next === "function"
  );
}

async function consumeProviderTextStream(options: {
  stream: ChatProviderTextStream;
  onChunk: (currentText: string) => void;
}): Promise<string> {
  const iterator = toAsyncIterator(options.stream);
  let text = "";

  while (true) {
    const next = await iterator.next();
    if (next.done) {
      break;
    }
    if (typeof next.value !== "string") {
      continue;
    }

    text += next.value;
    options.onChunk(text);
  }

  return text;
}

/**
 * Handles a single message returned (or yielded) by a {@link ChatProvider},
 * building a {@link ChatMessage} and handing it to `upsertMessage`. If the
 * message's text is itself a stream, it is consumed chunk by chunk, calling
 * `upsertMessage` with the same message ID each time so the caller can
 * upsert-in-place as the text grows.
 */
async function processProviderMessage(options: {
  message: ChatProviderMessageOptions;
  authorId: string;
  getParticipants: () => ChatParticipant[];
  i18n: i18n;
  upsertMessage: (message: ChatMessage) => void;
}): Promise<void> {
  const { message, authorId, getParticipants, i18n, upsertMessage } = options;

  if (message.type === "tool_call") {
    upsertMessage(
      createChatMessage(
        {
          type: "tool_call",
          name: message.name,
        },
        [authorId],
        [],
        { id: uuid(), timeMs: Date.now() }
      )
    );
  }

  if (message.type !== "text") {
    return;
  }

  if (typeof message.text !== "string") {
    const messageId = uuid();
    const messageTimeMs = Date.now();

    const upsertStreamingResponse = (text: string) => {
      const responseTargets = resolveMessageTargets(
        getParticipants(),
        text,
        i18n
      );
      upsertMessage(
        createChatMessage(
          { type: "text", text },
          [authorId],
          responseTargets.map((target) => target.id),
          { id: messageId, timeMs: messageTimeMs }
        )
      );
    };

    const finalText = await consumeProviderTextStream({
      stream: message.text,
      onChunk: upsertStreamingResponse,
    });
    upsertStreamingResponse(finalText);
    return;
  }

  const responseTargets = resolveMessageTargets(
    getParticipants(),
    message.text,
    i18n
  );
  upsertMessage(
    createChatMessage(
      { type: "text", text: message.text },
      [authorId],
      responseTargets.map((target) => target.id)
    )
  );
}

/**
 * Handles the full response returned by a {@link ChatProvider}'s
 * `generateResponse`: a single message, a stream of messages, or `null`.
 * Messages are processed sequentially — including fully consuming each
 * message's own text stream — before the next message is requested from the
 * provider's iterator.
 */
async function processProviderResponse(options: {
  response: ChatProviderResponse | null;
  authorId: string;
  getParticipants: () => ChatParticipant[];
  i18n: i18n;
  upsertMessage: (message: ChatMessage) => void;
}): Promise<void> {
  const { response, ...messageOptions } = options;
  if (!response) {
    return;
  }

  if (!isProviderMessageStream(response)) {
    await processProviderMessage({ message: response, ...messageOptions });
    return;
  }

  const iterator = toAsyncIterator(response);
  while (true) {
    const next = await iterator.next();
    if (next.done) {
      break;
    }
    if (!next.value) {
      continue;
    }
    await processProviderMessage({ message: next.value, ...messageOptions });
  }
}

function upsertMessageInList(
  messages: ChatMessage[],
  nextMessage: ChatMessage
) {
  const existingIndex = messages.findIndex(
    (message) => message.id === nextMessage.id
  );
  if (existingIndex < 0) {
    return [...messages, nextMessage];
  }
  return messages.map((message, index) =>
    index === existingIndex ? nextMessage : message
  );
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function extractMentionTokens(text: string): string[] {
  const tokens = new Set<string>();

  for (const match of text.matchAll(/(?:^|[^\w])@\{([^}]+)\}/g)) {
    const token = (match[1] ?? "").trim();
    if (token.length > 0) {
      tokens.add(token);
    }
  }

  for (const match of text.matchAll(/(?:^|[^\w])@([^\s@.,!?;:)}\]]+)/g)) {
    const token = (match[1] ?? "").trim();
    if (token.length > 0) {
      tokens.add(token);
    }
  }

  return Array.from(tokens);
}

function textIncludesMention(text: string, value: string): boolean {
  const token = value.trim();
  if (token.length === 0) {
    return false;
  }

  const escapedToken = escapeRegExp(token);
  const mentionPattern = new RegExp(
    `(?:^|[^\\w])@(?:\\{${escapedToken}\\}|${escapedToken})(?=$|[\\s.,!?;:)}\\]])`
  );
  return mentionPattern.test(text);
}

function textMentionsEveryone(text: string): boolean {
  return textIncludesMention(text, "everyone");
}

function resolveParticipantFromToken(
  token: string,
  participants: ChatParticipant[],
  participantIdAliases: Readonly<Record<string, string>>,
  t: (key: string) => string
): ChatParticipant | null {
  const byId = participants.find(
    (p) => p.id === token || p.id.slice(0, 6) === token
  );
  if (byId) return byId;

  const resolvedId = participantIdAliases[token];
  if (resolvedId) {
    const aliased = participants.find((p) => p.id === resolvedId);
    if (aliased) return aliased;
  }

  for (const participant of participants) {
    if (!participant.name) continue;
    const displayName =
      typeof participant.name === "string"
        ? participant.name
        : translateTitle(t, participant.name);
    if (displayName === token) return participant;
  }

  return null;
}

function parseTextMessage(
  message: TextChatMessage,
  participants: ChatParticipant[],
  participantIdAliases: Readonly<Record<string, string>>,
  i18n: i18n,
  books?: TranslationBook[]
): ParsedChatTextMessage {
  const { t } = i18n;
  const { text } = message;
  const mentionRegex = /(?<!\w)@(?:\{([^}]+)\}|([^\s@.,!?;:)}\]]+))/g;

  type PendingMatch =
    | { kind: "mention"; start: number; end: number; raw: RegExpMatchArray }
    | {
        kind: "verse_ref";
        start: number;
        end: number;
        ref: import("./BibleDataManager").VerseRef;
      };

  const pending: PendingMatch[] = [];

  for (const match of text.matchAll(mentionRegex)) {
    const token = (match[1] ?? match[2] ?? "").trim();
    if (!token) continue;
    pending.push({
      kind: "mention",
      start: match.index ?? 0,
      end: (match.index ?? 0) + match[0].length,
      raw: match,
    });
  }

  for (const { ref, start, end } of scanVerseReferencesInText(text, books)) {
    pending.push({ kind: "verse_ref", start, end, ref });
  }

  // Sort by position; on overlap keep the earlier-starting match.
  pending.sort((a, b) => a.start - b.start);

  const parts: ParsedTextPart[] = [];
  let lastIndex = 0;

  for (const m of pending) {
    if (m.start < lastIndex) continue; // skip overlapping matches
    if (lastIndex < m.start) {
      parts.push(text.slice(lastIndex, m.start));
    }

    if (m.kind === "mention") {
      parts.push({
        type: "mention",
        text: m.raw[0],
        participant: resolveParticipantFromToken(
          (m.raw[1] ?? m.raw[2] ?? "").trim(),
          participants,
          participantIdAliases,
          t
        ),
      });
    } else {
      parts.push({
        type: "verse_reference",
        text: text.slice(m.start, m.end),
        ref: m.ref,
      });
    }

    lastIndex = m.end;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return {
    type: "text",
    id: message.id,
    authors: message.authors,
    targets: message.targets,
    timeMs: message.timeMs,
    text,
    parts: parts.length > 0 ? parts : [text],
  };
}

export function resolveMessageTargets(
  participants: ChatParticipant[],
  text: string,
  i18n: i18n
): ChatParticipant[] {
  if (extractMentionTokens(text).length === 0) {
    return [];
  }

  const matches = new Map<string, ChatParticipant>();

  for (const participant of participants) {
    if (!participant.isActive) {
      continue;
    }
    if (
      textIncludesMention(text, participant.id) ||
      textIncludesMention(text, participant.id.slice(0, 6))
    ) {
      matches.set(participant.id, participant);
    }
  }

  for (const participant of participants) {
    if (!participant.isActive) {
      continue;
    }
    if (!participant.name) {
      continue;
    }

    const name = participant.name;
    if (typeof name === "string" && !textIncludesMention(text, name)) {
      continue;
    } else if (typeof name === "object") {
      const translatedName = translateTitle(i18n.t, name);
      if (!textIncludesMention(text, translatedName)) {
        continue;
      }
    }

    if (
      (participant.isRemote && !participant.isAI) ||
      (!participant.isRemote && participant.isAI)
    ) {
      matches.set(participant.id, participant);
    }
  }

  return Array.from(matches.values());
}

export function resolveMessageAuthors(
  participants: ChatParticipant[],
  message: ChatMessage,
  participantIdAliases: Readonly<Record<string, string>> = {}
): ChatParticipant[] {
  if (message.authors.length === 0) {
    return [];
  }

  const resolvedAuthors = new Map<string, ChatParticipant>();

  for (const authorId of message.authors) {
    let resolvedId: string | null = authorId;
    const visited = new Set<string>();

    while (resolvedId && participantIdAliases[resolvedId]) {
      if (visited.has(resolvedId)) {
        break;
      }
      visited.add(resolvedId);
      resolvedId = participantIdAliases[resolvedId] ?? null;
    }

    const participant = participants.find(
      (p) => p.id === (resolvedId ?? authorId)
    );
    if (participant) {
      resolvedAuthors.set(participant.id, participant);
    }
  }

  return Array.from(resolvedAuthors.values());
}

function resolveMessageTargetsWithAliases(
  participants: ChatParticipant[],
  text: string,
  participantIdAliases: Readonly<Record<string, string>>,
  i18n: i18n
): ChatParticipant[] {
  const matches = new Map<string, ChatParticipant>(
    resolveMessageTargets(participants, text, i18n).map((participant) => [
      participant.id,
      participant,
    ])
  );

  for (const token of extractMentionTokens(text)) {
    const resolvedId = participantIdAliases[token];
    if (!resolvedId) {
      continue;
    }

    const resolvedParticipant = participants.find((p) => p.id === resolvedId);
    if (resolvedParticipant) {
      matches.set(resolvedParticipant.id, resolvedParticipant);
    }
  }

  return Array.from(matches.values());
}

function stringArraysEqual(left: string[], right: string[]): boolean {
  return (
    left.length === right.length &&
    left.every((value, index) => value === right[index])
  );
}

function getUnreadMessagesSinceLastRead(
  messages: ChatMessage[],
  lastMessageRead: string | null
): ChatMessage[] {
  if (!lastMessageRead) {
    return messages;
  }

  const lastReadIndex = messages.findIndex(
    (message) => message.id === lastMessageRead
  );
  if (lastReadIndex < 0) {
    return messages;
  }

  return messages.slice(lastReadIndex + 1);
}

function getMostRecentProviderParticipant(
  participants: ChatParticipant[],
  messages: ChatMessage[]
): AIChatParticipant | null {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (!message) {
      continue;
    }

    for (
      let authorIndex = message.authors.length - 1;
      authorIndex >= 0;
      authorIndex -= 1
    ) {
      const authorId = message.authors[authorIndex];
      if (!authorId) {
        continue;
      }

      const participant = participants.find(
        (entry): entry is AIChatParticipant =>
          entry.isAI && !entry.isRemote && entry.id === authorId
      );
      if (participant) {
        return participant;
      }
    }
  }

  return (
    participants.find(
      (entry): entry is AIChatParticipant => entry.isAI && !entry.isRemote
    ) ?? null
  );
}

/**
 * The icon to show for an AI participant that someone else added to a shared
 * chat. A site-relative icon (like a bundled asset in dev,
 * `/packages/.../icon.svg`) only loads on the adder's own server, and an
 * adder on an older build shares no icon at all, so in both cases the
 * viewer's own copy of the same provider supplies it.
 */
function getSharedProviderIconUrl(
  sharedIconUrl: string | undefined,
  localIconUrl: string | undefined
): string | null {
  const isPortable =
    !!sharedIconUrl && /^[a-z][a-z0-9+.-]*:/i.test(sharedIconUrl);
  if (isPortable) {
    return sharedIconUrl;
  }
  return localIconUrl ?? sharedIconUrl ?? null;
}

function createSharedChatSession(
  session: BibleReadingSession,
  chatProviders: Signal<ChatProvider[]>,
  i18nManager: I18nManager,
  chatContext: ReadonlySignal<LocalChatContext>
): SharedChatSession {
  const i18n = i18nManager.i18n;
  const chats = session.document.getArray<unknown>("chats");
  const chatProvidersMap = session.document.getMap<unknown>("chat_providers");
  const participantAliasesMap = session.document.getMap<unknown>(
    "chat_participant_aliases"
  );
  const chatTypingMap = session.document.getMap<boolean>("chat_typing");
  const chatProvidersMapVersion = signal(0);
  const participantAliasesMapVersion = signal(0);
  const chatTypingMapVersion = signal(0);
  const participantIdAliases = signal<Record<string, string>>({});
  const localIsTyping = signal(false);
  const selectedProviderParticipantIds = signal<string[]>([]);
  chatProvidersMap.changes.subscribe(() => {
    // Avoid updating reactive graph synchronously during shared-doc
    // transactions to prevent dependency cycles.
    window.queueMicrotask(() => {
      chatProvidersMapVersion.value += 1;
    });
  });
  participantAliasesMap.changes.subscribe(() => {
    window.queueMicrotask(() => {
      participantAliasesMapVersion.value += 1;
    });
  });
  chatTypingMap.changes.subscribe(() => {
    window.queueMicrotask(() => {
      chatTypingMapVersion.value += 1;
    });
  });

  const readValidChats = (): ChatMessage[] => {
    const validMessages = chats
      .toArray()
      .map((rawMessage) => {
        const parsed = chatMessageSchema.safeParse(rawMessage);
        return parsed.success ? parsed.data : null;
      })
      .filter((message): message is ChatMessage => message !== null);

    // Keep only the latest version of each message ID to support streaming
    // updates when a shared array backend cannot replace items in place.
    const deduped = new Map<string, ChatMessage>();
    for (const message of validMessages) {
      deduped.set(message.id, message);
    }
    return Array.from(deduped.values()).sort((a, b) => a.timeMs - b.timeMs);
  };

  const messages = signal<ChatMessage[]>(readValidChats());
  const lastMessageRead = signal<string | null>(null);
  chats.changes.subscribe(() => {
    messages.value = readValidChats();
  });

  const upsertSharedMessage = (nextMessage: ChatMessage) => {
    session.document.transact(() => {
      const existingMessages = chats.toArray();
      const existingIndex = existingMessages.findIndex(
        (entry) =>
          typeof entry === "object" &&
          entry !== null &&
          (entry as { id?: unknown }).id === nextMessage.id
      );

      if (existingIndex >= 0 && typeof chats.delete === "function") {
        chats.delete(existingIndex, 1);
        if (typeof chats.insert === "function") {
          chats.insert(existingIndex, [nextMessage]);
          return;
        }
      }

      chats.push(nextMessage);
    });
  };

  const unreadMessages = computed(() =>
    getUnreadMessagesSinceLastRead(messages.value, lastMessageRead.value)
  );

  const markAsRead = (messageId?: string) => {
    const msgs = messages.value;
    if (messageId !== undefined) {
      const newIndex = msgs.findIndex((m) => m.id === messageId);
      if (newIndex < 0) return;
      const currentIndex = lastMessageRead.value
        ? msgs.findIndex((m) => m.id === lastMessageRead.value)
        : -1;
      if (newIndex > currentIndex) {
        lastMessageRead.value = messageId;
      }
    } else {
      lastMessageRead.value = msgs.at(-1)?.id ?? null;
    }
  };

  effect(() => {
    void participantAliasesMapVersion.value;
    const nextAliases: Record<string, string> = {};
    participantAliasesMap.forEach((value, key) => {
      if (typeof key !== "string" || typeof value !== "string") {
        return;
      }
      nextAliases[key] = value;
    });
    participantIdAliases.value = nextAliases;
  });

  let previousParticipantIdByConnectionId = new Map<string, string>();
  effect(() => {
    const nextParticipantIdByConnectionId = new Map<string, string>();
    const aliases = {
      ...participantIdAliases.value,
    };
    let aliasesChanged = false;

    for (const connectedUser of session.connectedUsers.value) {
      const connectionId = connectedUser.connectionId ?? null;
      const userId = connectedUser.userId ?? null;
      const currentParticipantId = userId ?? connectionId;

      if (!connectionId || !currentParticipantId) {
        continue;
      }

      const previousParticipantId =
        previousParticipantIdByConnectionId.get(connectionId) ?? null;
      if (
        previousParticipantId &&
        previousParticipantId !== currentParticipantId &&
        previousParticipantId === connectionId
      ) {
        const existingSharedAlias = participantAliasesMap.get(
          previousParticipantId
        );
        if (existingSharedAlias !== currentParticipantId) {
          participantAliasesMap.set(
            previousParticipantId,
            currentParticipantId
          );
        }
        if (
          participantIdAliases.value[previousParticipantId] !==
          currentParticipantId
        ) {
          aliases[previousParticipantId] = currentParticipantId;
          aliasesChanged = true;
        }
      }

      nextParticipantIdByConnectionId.set(connectionId, currentParticipantId);
    }

    previousParticipantIdByConnectionId = nextParticipantIdByConnectionId;
    if (aliasesChanged) {
      participantIdAliases.value = aliases;
    }
  });

  const allUserParticipants = computed<UserChatParticipant[]>(() =>
    groupConnectedUsers(session.allUsers.value).map(
      (group): UserChatParticipant => ({
        id: group.id,
        userId: group.userId,
        connectionId: group.connectionId,
        profile: group.profile,
        sessionUser: group.sessionUser,
        visual: group.sessionUser?.visual ?? getUserAnimalVisual(group.id),
        name: group.name,
        isSelf: group.isSelf,
        isAI: false,
        isRemote: !group.isSelf,
        isActive: group.isActive,
        joinTimeMs: group.joinedAtMs ?? 0,
      })
    )
  );

  const localParticipantId = computed(
    () =>
      session.currentUser.value?.userId ??
      session.currentUser.value?.connectionId ??
      null
  );

  const localParticipant = computed(
    () => allUserParticipants.value.find((p) => p.isSelf) ?? null
  );

  const sharedProviderParticipants = computed<AIChatParticipant[]>(() => {
    const currentLocalParticipantId = localParticipantId.value;
    const users = allUserParticipants.value;
    void chatProvidersMapVersion.value;

    const providerParticipants: AIChatParticipant[] = [];
    chatProvidersMap.forEach((value, ownerParticipantId) => {
      const parsed = sharedAIChatParticipantArraySchema.safeParse(value);
      if (!parsed.success) {
        console.error(
          "Invalid chat provider data for participant ID",
          ownerParticipantId,
          parsed.error
        );
        return;
      }
      const owner = users.find((p) => p.id === ownerParticipantId);
      if (!owner) {
        console.error("Owner not found for shared participant", value);
        return;
      }
      const isRemote = ownerParticipantId !== currentLocalParticipantId;

      for (const p of parsed.data) {
        if (!isRemote) {
          const localParticipants = localProviderParticipants.peek();
          const localParticipant = localParticipants.find((l) => l.id === p.id);

          if (localParticipant) {
            providerParticipants.push(localParticipant);
            continue;
          }
        }

        providerParticipants.push({
          ...p,
          iconUrl: getSharedProviderIconUrl(
            p.iconUrl,
            chatProviders.value.find((provider) => provider.id === p.providerId)
              ?.iconUrl
          ),
          ownerParticipantId,
          userId: owner.userId,
          connectionId: owner.connectionId,
          isSelf: false,
          isRemote,
          isActive: owner.isActive,
          // AI participants share their owner's join time.
          joinTimeMs: owner.joinTimeMs,
        });
      }
    });

    return providerParticipants;
  });

  const totalParticipants = computed(() => {
    return [...allUserParticipants.value, ...sharedProviderParticipants.value];
  });

  const participants = computed(() =>
    totalParticipants.value.filter((p) => p.isActive)
  );

  const inactiveParticipants = computed(() =>
    totalParticipants.value.filter((p) => !p.isActive)
  );

  const typingParticipants = computed(() => {
    void chatTypingMapVersion.value;
    const typingParticipantIds = new Set<string>();

    chatTypingMap.forEach((value, participantId) => {
      if (value) {
        typingParticipantIds.add(participantId);
      }
    });

    return participants.value.filter(
      (participant) =>
        participant.isActive && typingParticipantIds.has(participant.id)
    );
  });

  const localProviderParticipants = computed<AIChatParticipant[]>(() => {
    const p = localParticipant.value;
    if (!p) {
      return [];
    }
    return chatProviders.value
      .filter((p) => p.supportsSharedChats)
      .map((provider) => {
        const id = createProviderParticipantId(
          p.connectionId ?? p.id,
          provider.id
        );
        return {
          id,
          providerId: provider.id,
          ownerParticipantId: p.id,
          userId: p.userId ?? null,
          connectionId: p.connectionId ?? null,
          name: provider.name,
          iconUrl: provider.iconUrl ?? null,
          isSelf: false,
          isAI: true,
          isRemote: false,
          isActive: p.isActive,
          // AI participants share their owner's join time.
          joinTimeMs: p.joinTimeMs,
        };
      });
  });

  const selectedProviderParticipants = computed(() => {
    const selectedIds = new Set(selectedProviderParticipantIds.value);
    return localProviderParticipants.value.filter((participant) =>
      selectedIds.has(participant.id)
    );
  });

  const availableParticipants = computed<ChatParticipant[]>(() => {
    const currentLocalParticipantId = localParticipantId.value;
    if (!currentLocalParticipantId) {
      return [];
    }

    const selectedIds = new Set(selectedProviderParticipantIds.value);
    return localProviderParticipants.value.filter(
      (p) => !selectedIds.has(p.id)
    );
  });

  effect(() => {
    if (!localParticipantId.value) {
      return;
    }
    const isTyping = localIsTyping.value;
    chatTypingMap.set(localParticipantId.value, isTyping);
  });

  let previousLocalParticipantId: string | null = null;
  effect(() => {
    const currentLocalParticipantId = localParticipantId.value;

    session.document.transact(() => {
      if (
        previousLocalParticipantId &&
        previousLocalParticipantId !== currentLocalParticipantId
      ) {
        chatProvidersMap.delete(previousLocalParticipantId);
      }

      if (!currentLocalParticipantId) {
        if (previousLocalParticipantId) {
          chatProvidersMap.delete(previousLocalParticipantId);
        }
        previousLocalParticipantId = null;
        return;
      }

      // const existingParticipants = sharedAIChatParticipantArraySchema.safeParse(
      //   chatProvidersMap.get(currentLocalParticipantId)
      // );
      // const currentValue = existingParticipants.success
      //   ? existingParticipants.data
      //   : [];
      // if (!participantsMatch(currentValue, localProviderParticipants.value)) {
      // }
      chatProvidersMap.set(
        currentLocalParticipantId,
        selectedProviderParticipants.value.map(
          (p): SharedAIChatParticipant => ({
            id: p.id,
            providerId: p.providerId,
            name: p.name ? translateTitle(i18n.t, p.name) : null,
            isAI: p.isAI,
            ...(p.iconUrl ? { iconUrl: p.iconUrl } : {}),
          })
        )
      );

      previousLocalParticipantId = currentLocalParticipantId;
    });
  });

  const addSharedProviderParticipant = (participantId: string) => {
    const currentLocalParticipantId = localParticipantId.value;
    if (!currentLocalParticipantId) {
      return;
    }

    const localProvider = localProviderParticipants.value.find(
      (participant) => participant.id === participantId
    );
    if (!localProvider) {
      return;
    }

    if (selectedProviderParticipantIds.value.includes(participantId)) {
      return;
    }

    selectedProviderParticipantIds.value = [
      ...selectedProviderParticipantIds.value,
      participantId,
    ];

    const provider = chatProviders.value.find(
      (entry) => entry.id === localProvider.providerId
    );
    if (provider?.onJoinChat) {
      const merged = chatContext.value;
      provider.onJoinChat({
        chatId,
        messages: messages.value,
        participants: participants.value,
        instructions: merged.instructions,
        tools: merged.tools,
      });
    }
  };

  const setParticipantTyping = (participantId: string, isTyping: boolean) => {
    session.document.transact(() => {
      if (isTyping) {
        chatTypingMap.set(participantId, true);
      } else {
        chatTypingMap.delete(participantId);
      }
    });
  };

  const sendMessage = async (message: ChatMessageOptions) => {
    const authorId =
      session.currentUser.value?.userId ??
      session.currentUser.value?.connectionId ??
      null;

    const isEveryoneMentioned =
      message.type === "text" && textMentionsEveryone(message.text);

    // Auto-add available participants that are mentioned before resolving targets
    const mentionedAvailable =
      !isEveryoneMentioned && message.type === "text"
        ? resolveMessageTargetsWithAliases(
            availableParticipants.value,
            message.text,
            participantIdAliases.value,
            i18n
          )
        : [];
    for (const newParticipant of mentionedAvailable) {
      addSharedProviderParticipant(newParticipant.id);
    }

    const targetParticipants: ChatParticipant[] = isEveryoneMentioned
      ? participants.value.filter((p) => p.isActive && p.isAI && !p.isRemote)
      : message.type === "text"
        ? resolveMessageTargetsWithAliases(
            [...participants.value, ...mentionedAvailable],
            message.text,
            participantIdAliases.value,
            i18n
          )
        : [];
    const messageTargets: string[] | true = isEveryoneMentioned
      ? true
      : targetParticipants.map((participant) => participant.id);
    const nextMessage = createChatMessage(
      message,
      authorId ? [authorId] : [],
      messageTargets
    );

    chats.push(nextMessage);

    for (const participant of targetParticipants) {
      void (async () => {
        if (!participant.isAI || participant.isRemote || !authorId) {
          return;
        }

        const provider = chatProviders.value.find(
          (entry) => entry.id === participant.providerId
        );
        if (!provider) {
          return;
        }

        setParticipantTyping(participant.id, true);

        try {
          const merged = chatContext.value;
          const response = await provider.generateResponse({
            chatId,
            messages: [...messages.value, nextMessage],
            participant,
            participants: participants.value,
            instructions: merged.instructions,
            tools: merged.tools,
          });
          await processProviderResponse({
            response,
            authorId: participant.id,
            getParticipants: () => participants.value,
            i18n,
            upsertMessage: upsertSharedMessage,
          });
        } catch (err) {
          upsertSharedMessage(
            createChatMessage(
              {
                type: "text",
                text: i18n.t("chat-ai-error", {
                  defaultValue:
                    "Sorry, something went wrong generating a response ({{error}}).",
                  error: err instanceof Error ? err.message : String(err),
                }),
              },
              [participant.id],
              []
            )
          );
        } finally {
          setParticipantTyping(participant.id, false);
        }
      })();
    }
  };

  const appendMessage = (
    message: ChatMessageOptions,
    authors: string[] = []
  ) => {
    chats.push(createChatMessage(message, authors, []));
  };

  const parsedMessages = computed<ParsedChatTextMessage[]>(() => {
    // Test doubles and partial session mocks may omit reading state; fall back
    // to English-only resolution via getBookId when books are unavailable.
    const books = session.readingState?.translationBooks?.value?.books;
    return messages.value
      .filter((m): m is TextChatMessage => m.type === "text")
      .map((m) =>
        parseTextMessage(
          m,
          totalParticipants.value,
          participantIdAliases.value,
          i18n,
          books
        )
      );
  });

  const wasMentioned = getWasMentionedSignal(participants, unreadMessages);

  const chatId = session.id;

  return {
    id: chatId,
    messages,
    parsedMessages,
    unreadMessages,
    lastMessageRead,
    wasMentioned,
    markAsRead,
    sendMessage,
    appendMessage,
    translationSuggestion: signal<TranslationSuggestion | null>(null),
    setTypingStatus: (isTyping: boolean) => {
      localIsTyping.value = isTyping;
    },
    participants,
    totalParticipants,
    inactiveParticipants,
    availableParticipants,
    typingParticipants,
    addParticipant: addSharedProviderParticipant,
    removeParticipant: (participantId: string) => {
      const currentLocalParticipantId = localParticipantId.value;
      if (!currentLocalParticipantId) {
        return;
      }

      const localProvider = sharedProviderParticipants.value.find(
        (participant) =>
          participant.id === participantId &&
          participant.ownerParticipantId === currentLocalParticipantId
      );
      if (!localProvider) {
        return;
      }

      selectedProviderParticipantIds.value =
        selectedProviderParticipantIds.value.filter(
          (id) => id !== participantId
        );

      setParticipantTyping(participantId, false);

      const provider = chatProviders.value.find(
        (entry) => entry.id === localProvider.providerId
      );
      if (provider && provider.onLeaveChat) {
        const merged = chatContext.value;
        provider.onLeaveChat({
          chatId,
          messages: messages.value,
          participants: participants.value,
          instructions: merged.instructions,
          tools: merged.tools,
        });
      }
    },
    getMessageAuthors: (message: ChatMessage) =>
      resolveMessageAuthors(
        totalParticipants.value,
        message,
        participantIdAliases.value
      ),
    context: chatContext,
    unsentDraft: signal(""),
    isShared: true,
    session,
  };
}

function getWasMentionedSignal(
  participants: ReadonlySignal<(UserChatParticipant | AIChatParticipant)[]>,
  unreadMessages: ReadonlySignal<ChatMessage[]>
) {
  return computed(() => {
    const selfParticipantIds = new Set(
      participants.value
        .filter((participant) => participant.isSelf)
        .map((participant) => participant.id)
    );
    if (selfParticipantIds.size === 0) {
      return false;
    }

    const unread = unreadMessages.value;
    if (
      unread.some(
        (message) =>
          message.targets === true ||
          message.targets.some((targetId) => selfParticipantIds.has(targetId))
      )
    ) {
      return true;
    }
    return false;
  });
}

function createLocalChatSession(
  loginManager: LoginManager,
  chatProviders: Signal<ChatProvider[]>,
  i18nManager: I18nManager,
  chatContext: ReadonlySignal<LocalChatContext>,
  history?: ChatSessionHistory,
  translationBooks?: ReadonlySignal<TranslationBook[] | undefined>
): ChatSession {
  const i18n = i18nManager.i18n;
  // Join times keyed by participant id, recorded the first time each id is seen.
  const participantJoinTimes = signal<Record<string, number>>({});
  const localParticipant = computed<UserChatParticipant>(() => {
    const id = loginManager.userId.value ?? DEFAULT_LOCAL_PARTICIPANT_ID;
    return {
      id,
      userId: loginManager.userId.value ?? null,
      connectionId: null,
      profile: loginManager.profile.value,
      name: getParticipantName(loginManager.profile.value),
      visual: getUserAnimalVisual(
        getConnectedUserVisualKey({
          userId: loginManager.userId.value,
          connectionId: loginManager.connectionId,
        })
      ),
      isSelf: true,
      isAI: false,
      isRemote: false,
      isActive: true,
      joinTimeMs: participantJoinTimes.value[id] ?? 0,
    };
  });
  const allProviderParticipants = computed<AIChatParticipant[]>(() =>
    chatProviders.value.map((provider) => ({
      id: provider.id,
      providerId: provider.id,
      ownerParticipantId: localParticipant.value.id,
      userId: loginManager.userId.value ?? null,
      connectionId: null,
      name: provider.name,
      iconUrl: provider.iconUrl ?? null,
      isSelf: false,
      isAI: true,
      isRemote: false,
      isActive: localParticipant.value.isActive,
      joinTimeMs: participantJoinTimes.value[provider.id] ?? 0,
    }))
  );
  const selectedProviderParticipantIds = signal<string[]>(
    history?.providerIds ?? []
  );
  const providerTypingParticipantIds = signal<string[]>([]);

  // The set of participant ids to track join times for, derived only from raw
  // sources (the local user id and the selected providers) so the recording
  // effect below never depends on the participant computeds it feeds.
  const trackedParticipantIds = computed(() => {
    const ids = new Set<string>();
    ids.add(loginManager.userId.value ?? DEFAULT_LOCAL_PARTICIPANT_ID);
    const validProviderIds = new Set(chatProviders.value.map((p) => p.id));
    for (const id of selectedProviderParticipantIds.value) {
      if (validProviderIds.has(id)) {
        ids.add(id);
      }
    }
    return ids;
  });

  effect(() => {
    const now = Date.now();
    const current = participantJoinTimes.value;
    let next: Record<string, number> | null = null;
    for (const id of trackedParticipantIds.value) {
      if (current[id] === undefined) {
        next ??= { ...current };
        next[id] = now;
      }
    }
    if (next) {
      participantJoinTimes.value = next;
    }
  });

  effect(() => {
    const validIds = new Set(
      allProviderParticipants.value.map((participant) => participant.id)
    );
    const nextSelectedProviderParticipantIds =
      selectedProviderParticipantIds.value.filter((participantId) =>
        validIds.has(participantId)
      );
    if (
      !stringArraysEqual(
        selectedProviderParticipantIds.value,
        nextSelectedProviderParticipantIds
      )
    ) {
      selectedProviderParticipantIds.value = nextSelectedProviderParticipantIds;
    }

    const nextTypingParticipantIds = providerTypingParticipantIds.value.filter(
      (participantId) =>
        validIds.has(participantId) &&
        nextSelectedProviderParticipantIds.includes(participantId)
    );
    if (
      !stringArraysEqual(
        providerTypingParticipantIds.value,
        nextTypingParticipantIds
      )
    ) {
      providerTypingParticipantIds.value = nextTypingParticipantIds;
    }
  });

  const totalParticipants = computed<ChatParticipant[]>(() => {
    const selectedIds = new Set(selectedProviderParticipantIds.value);
    return [
      localParticipant.value,
      ...allProviderParticipants.value.filter((participant) =>
        selectedIds.has(participant.id)
      ),
    ];
  });

  const participants = computed<ChatParticipant[]>(() =>
    totalParticipants.value.filter((p) => p.isActive)
  );

  const inactiveParticipants = computed(() =>
    totalParticipants.value.filter((p) => !p.isActive)
  );

  const availableParticipants = computed<ChatParticipant[]>(() => {
    const selectedIds = new Set(selectedProviderParticipantIds.value);
    return allProviderParticipants.value.filter(
      (participant) => !selectedIds.has(participant.id)
    );
  });
  const messages = signal<ChatMessage[]>(history?.messages ?? []);
  const lastMessageRead = signal<string | null>(null);
  const localIsTyping = signal(false);
  const participantIdAliases = signal<Record<string, string>>({});

  let previousLocalParticipantId: string | null = null;
  effect(() => {
    const currentLocalParticipantId = localParticipant.value.id;
    if (
      previousLocalParticipantId &&
      previousLocalParticipantId !== currentLocalParticipantId &&
      participantIdAliases.value[previousLocalParticipantId] !==
        currentLocalParticipantId
    ) {
      participantIdAliases.value = {
        ...participantIdAliases.value,
        [previousLocalParticipantId]: currentLocalParticipantId,
      };
    }
    previousLocalParticipantId = currentLocalParticipantId;
  });

  const typingParticipants = computed<ChatParticipant[]>(() => {
    const typing = new Map<string, ChatParticipant>();
    if (localIsTyping.value) {
      typing.set(localParticipant.value.id, localParticipant.value);
    }

    const providerIds = new Set(providerTypingParticipantIds.value);
    for (const participant of participants.value) {
      if (participant.isAI && providerIds.has(participant.id)) {
        typing.set(participant.id, participant);
      }
    }

    return Array.from(typing.values());
  });

  const addLocalProviderParticipant = (participantId: string) => {
    const isKnownProvider = allProviderParticipants.value.some(
      (participant) => participant.id === participantId
    );
    if (!isKnownProvider) {
      return;
    }

    if (selectedProviderParticipantIds.value.includes(participantId)) {
      return;
    }

    if (participantJoinTimes.value[participantId] === undefined) {
      participantJoinTimes.value = {
        ...participantJoinTimes.value,
        [participantId]: Date.now(),
      };
    }

    selectedProviderParticipantIds.value = [
      ...selectedProviderParticipantIds.value,
      participantId,
    ];

    const provider = chatProviders.value.find(
      (entry) => entry.id === participantId
    );
    if (provider?.onJoinChat) {
      const merged = chatContext.value;
      provider.onJoinChat({
        chatId,
        messages: messages.value,
        participants: participants.value,
        instructions: merged.instructions,
        tools: merged.tools,
      });
    }
  };

  const sendMessage = async (message: ChatMessageOptions) => {
    const participant = localParticipant.value;

    const isEveryoneMentioned =
      message.type === "text" && textMentionsEveryone(message.text);

    // Auto-add available participants that are mentioned
    if (message.type === "text") {
      const toAdd = isEveryoneMentioned
        ? availableParticipants.value
        : resolveMessageTargets(
            availableParticipants.value,
            message.text,
            i18n
          );
      for (const newParticipant of toAdd) {
        addLocalProviderParticipant(newParticipant.id);
      }
    }

    let messageTargets: string[] | true;
    let providerTargets: AIChatParticipant[];
    if (isEveryoneMentioned) {
      messageTargets = true;
      providerTargets = participants.value.filter(
        (p): p is AIChatParticipant => p.isAI && !p.isRemote && p.isActive
      );
    } else {
      const resolvedTargetParticipants =
        message.type === "text"
          ? resolveMessageTargets(participants.value, message.text, i18n)
          : [];
      const defaultProviderParticipant = getMostRecentProviderParticipant(
        participants.value,
        messages.value
      );
      const targetParticipants =
        resolvedTargetParticipants.length > 0
          ? resolvedTargetParticipants
          : defaultProviderParticipant
            ? [defaultProviderParticipant]
            : [];
      messageTargets = targetParticipants.map((target) => target.id);
      providerTargets = targetParticipants.filter(
        (target): target is AIChatParticipant => target.isAI && !target.isRemote
      );
    }
    const nextMessage = createChatMessage(
      message,
      participant.id ? [participant.id] : [],
      messageTargets
    );

    messages.value = [...messages.value, nextMessage];

    for (const target of providerTargets) {
      void (async () => {
        const provider = chatProviders.value.find(
          (entry) => entry.id === target.id
        );
        if (!provider) {
          return;
        }

        providerTypingParticipantIds.value = Array.from(
          new Set([...providerTypingParticipantIds.value, target.id])
        );

        try {
          const merged = chatContext.value;
          const response = await provider.generateResponse({
            chatId,
            messages: [...messages.value],
            participant: target,
            participants: participants.value,
            instructions: merged.instructions,
            tools: merged.tools,
          });
          await processProviderResponse({
            response,
            authorId: target.id,
            getParticipants: () => participants.value,
            i18n,
            upsertMessage: (message) => {
              messages.value = upsertMessageInList(messages.value, message);
            },
          });
        } catch (err) {
          messages.value = upsertMessageInList(
            messages.value,
            createChatMessage(
              {
                type: "text",
                text: i18n.t("chat-ai-error", {
                  defaultValue:
                    "Sorry, something went wrong generating a response ({{error}}).",
                  error: err instanceof Error ? err.message : String(err),
                }),
              },
              [target.id],
              []
            )
          );
        } finally {
          providerTypingParticipantIds.value =
            providerTypingParticipantIds.value.filter((id) => id !== target.id);
        }
      })();
    }
  };

  const appendMessage = (
    message: ChatMessageOptions,
    authors: string[] = []
  ) => {
    const nextMessage = createChatMessage(message, authors, []);
    messages.value = [...messages.value, nextMessage];
  };

  const getMessageAuthors = (message: ChatMessage) =>
    resolveMessageAuthors(
      totalParticipants.value,
      message,
      participantIdAliases.value
    );

  const markAsRead = (messageId?: string) => {
    const msgs = messages.value;
    if (messageId !== undefined) {
      const newIndex = msgs.findIndex((m) => m.id === messageId);
      if (newIndex < 0) return;
      const currentIndex = lastMessageRead.value
        ? msgs.findIndex((m) => m.id === lastMessageRead.value)
        : -1;
      if (newIndex > currentIndex) {
        lastMessageRead.value = messageId;
      }
    } else {
      lastMessageRead.value = msgs.at(-1)?.id ?? null;
    }
  };

  const unreadMessages = computed(() =>
    getUnreadMessagesSinceLastRead(messages.value, lastMessageRead.value)
  );

  const parsedMessages = computed<ParsedChatTextMessage[]>(() => {
    const books = translationBooks?.value;
    return messages.value
      .filter((m): m is TextChatMessage => m.type === "text")
      .map((m) =>
        parseTextMessage(
          m,
          totalParticipants.value,
          participantIdAliases.value,
          i18n,
          books
        )
      );
  });

  const wasMentioned = getWasMentionedSignal(participants, unreadMessages);
  const chatId = uuid();

  return {
    id: chatId,
    messages,
    parsedMessages,
    unreadMessages,
    lastMessageRead,
    wasMentioned,
    markAsRead,
    sendMessage,
    appendMessage,
    translationSuggestion: signal<TranslationSuggestion | null>(null),
    setTypingStatus: (isTyping: boolean) => {
      localIsTyping.value = isTyping;
    },
    participants,
    totalParticipants,
    inactiveParticipants,
    availableParticipants,
    typingParticipants,
    addParticipant: addLocalProviderParticipant,
    removeParticipant: (participantId: string) => {
      const nextSelectedProviderParticipantIds =
        selectedProviderParticipantIds.value.filter(
          (id) => id !== participantId
        );
      if (
        stringArraysEqual(
          selectedProviderParticipantIds.value,
          nextSelectedProviderParticipantIds
        )
      ) {
        return;
      }

      selectedProviderParticipantIds.value = nextSelectedProviderParticipantIds;
      providerTypingParticipantIds.value =
        providerTypingParticipantIds.value.filter((id) => id !== participantId);

      const provider = chatProviders.value.find(
        (entry) => entry.id === participantId
      );
      if (provider && provider.onLeaveChat) {
        const merged = chatContext.value;
        provider.onLeaveChat({
          chatId,
          messages: messages.value,
          participants: participants.value,
          instructions: merged.instructions,
          tools: merged.tools,
        });
      }
    },
    getMessageAuthors,
    context: chatContext,
    unsentDraft: signal(""),
  };
}

export function createChatsManager(
  loginManager: LoginManager,
  i18nManager: I18nManager,
  /**
   * Books for the currently open reader tab. Used so local chat can resolve
   * localized scripture names (shared chats read books from the session's
   * reading state instead).
   */
  translationBooks?: ReadonlySignal<TranslationBook[] | undefined>
): ChatsManager {
  const chats = signal<ChatSession[]>([]);
  const isOpen = signal<boolean>(false);
  const chatProviders = signal<ChatProvider[]>([]);

  // The custom context (instructions + tools) shared by every chat session.
  // It is kept in-memory only: it is local to this client and is never
  // broadcast to remote participants (tool `function` callbacks are not
  // serializable and only ever run in the local runtime).
  const defaultContext = signal<LocalChatContext>({});
  const additionalContexts = signal<IdentifiedLocalChatContext[]>([]);
  const context = computed<LocalChatContext>(() =>
    mergeLocalChatContexts([defaultContext.value, ...additionalContexts.value])
  );
  // Reads via `.peek()` (not `.value`) when deriving the next value from the
  // current one: these are called from reactive effects that toggle a
  // context on/off (e.g. `PlaylistManager` while a playlist is being edited),
  // and a tracked read here would make `additionalContexts`/`defaultContext` a
  // dependency of that calling effect — which its own write to the same
  // signal would then re-trigger, looping forever.
  const setContext = (next: LocalChatContext) => {
    defaultContext.value = { ...defaultContext.peek(), ...next };
  };
  const addContext = (next: IdentifiedLocalChatContext) => {
    additionalContexts.value = [
      ...additionalContexts.peek().filter((c) => c.id !== next.id),
      next,
    ];
  };
  const removeContext = (id: string) => {
    additionalContexts.value = additionalContexts
      .peek()
      .filter((c) => c.id !== id);
  };
  const composerDraft = signal("");
  const selectedChatId = signal<string | null>(null);
  const selectedChat = computed(
    () => chats.value.find((chat) => chat.id === selectedChatId.value) ?? null
  );
  const numberOfUnreadMessages = computed(() => {
    return chats.value.reduce(
      (acc, chat) => acc + chat.unreadMessages.value.length,
      0
    );
  });

  const wasMentioned = computed(() =>
    chats.value.some((chat) => chat.wasMentioned.value)
  );

  const registerProvider = (provider: ChatProvider) => {
    chatProviders.value = [
      ...chatProviders.value.filter((p) => p.id !== provider.id),
      provider,
    ];

    return () => {
      const currentProvider = chatProviders.value.find(
        (p) => p.id === provider.id
      );
      if (currentProvider !== provider) {
        return;
      }

      for (const chat of chats.value) {
        const participant = chat.participants.value.find(
          (p) => p.isAI && !p.isRemote && p.providerId === provider.id
        );
        if (participant) {
          chat.removeParticipant(participant.id);
        }
      }

      chatProviders.value = chatProviders.value.filter(
        (p) => p.id !== provider.id
      );
    };
  };

  const createLocalSession = (history?: ChatSessionHistory) => {
    const chat = createLocalChatSession(
      loginManager,
      chatProviders,
      i18nManager,
      context,
      history,
      translationBooks
    );
    chats.value = [...chats.value, chat];
    return chat;
  };

  const createSharedSession = (session: BibleReadingSession) => {
    const chat = createSharedChatSession(
      session,
      chatProviders,
      i18nManager,
      context
    );
    chats.value = [...chats.value, chat];
    return chat;
  };

  return {
    isOpen,
    chats,
    providers: chatProviders,
    numberOfUnreadMessages,
    wasMentioned,
    createSharedSession,
    createLocalSession,
    registerProvider,
    selectedChat,
    selectChat: (chatId: string | null) => {
      selectedChatId.value = chatId;
    },
    context,
    activeContexts: additionalContexts,
    setContext,
    addContext,
    removeContext,
    composerDraft,
  };
}
